/**
 * UserInputInlineRow — inline timeline-style renderer for agent
 * user-input requests. Replaces ChatApprovalModal (2026-04-23) so
 * the approval surface lives in the conversation feed instead of
 * a full-screen modal with a backdrop.
 *
 * Philosophy (Loom/Thread):
 *   Timeline IS the chat. Approvals from `request_user_input`
 *   (WriteGate, LiteratureSearchGate, future `request_user_input`
 *   calls by the drafting agent, and anything else routed through
 *   the WebSocket bridge) are typed rows in that feed — not
 *   takeover modals.
 *
 * Dispatch pattern:
 *   Ported from the classic-UI ApprovalCard. Inspects
 *   ``request.inputType`` and picks the right body view:
 *     - ``api_failure``               → ApiFailureView
 *     - everything else (literature   → JsonForm (generic
 *       search, generic request_user_input, any future kind)
 *
 *   ``write_approval`` requests with a parseable payload NO LONGER
 *   render here — they render in the chat thread through the shared
 *   ProposalCard (WriteApprovalMessage), the same surface highlight
 *   edits use, so every text-edit decision looks identical. The
 *   shell suppresses this row for that case; an unparseable
 *   write_approval payload still falls through to the generic
 *   branch below as a defensive fallback.
 *
 *   The body views are re-used AS-IS from
 *   ``addin/src/components/*`` — they're pure presentation and
 *   don't know where they're rendered.
 *
 * # DECISION: inline row, not a modal. Retires ChatApprovalModal
 * #   from Variant C's render path.
 * # WHY: writer feedback (2026-04-23) + Loom/Thread philosophy:
 * #      "timeline IS the chat" means approvals should flow in
 * #      the conversation, not interrupt it with a backdrop.
 * #      Modals also compete with the Spine-tab + LibraryHub
 * #      dialogs already in the UI — overlapping popovers feel
 * #      chaotic. An inline card keeps the writer's scroll
 * #      position + context.
 * # DATE: 2026-04-23
 *
 * # DECISION: re-use WriteApprovalView / ApiFailureView / JsonForm
 * #   verbatim instead of rebuilding them with Loom/Thread tokens.
 * # WHY: those views are pure presentation; their styling is
 * #      consistent with approval visuals across the product
 * #      (yellow warning treatment signals "waiting on you"). A
 * #      Loom/Thread-specific rebuild would drift — the backend
 * #      contract is the same, and the content (diff cards,
 * #      params forms) doesn't need re-theming to carry its
 * #      meaning. The outer shell gets Loom/Thread treatment;
 * #      the bodies are unchanged.
 * # DATE: 2026-04-23
 *
 * SRP: presentation + dispatch only. Receives a ``UserInputRequest``
 * and two callbacks. Does NOT manage the bridge, does NOT append
 * to the event log. The shell handles those; this component is
 * drop-in.
 */

import { Button, makeStyles } from "@fluentui/react-components";
import { CheckmarkRegular, CodeRegular, DismissRegular, FormRegular } from "@fluentui/react-icons";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import ApiFailureView, { parseApiFailurePayload } from "../../components/ApiFailureView";
import JsonForm, { type JsonValue } from "../../components/JsonForm";
import { stripJsonTail } from "../../components/WriteApprovalView";
import type { UserInputRequest } from "../../services/documentBridge";
import { brandColors, fonts, inkColors, motion, surfaceColors, typeScale } from "../tokens";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface UserInputInlineRowProps {
  /** The pending request from ``useUserInputBridge``. Null hides
   *  the row entirely — the component returns null so no empty
   *  chrome sits between the timeline and the rail. */
  request: UserInputRequest | null;
  /** Approve with a value. Shape depends on inputType:
   *   - write_approval  → ``JSON.stringify({ approved: true, content })``
   *   - api_failure     → ``JSON.stringify({ action })``
   *   - else            → serialised form value (or raw JSON)
   *  The backend's response parser handles all three. */
  onApprove: (value: string) => void;
  /** Reject explicitly. Bridge sends an empty string; backend
   *  treats it as ``Decision(approved=False)``. */
  onReject: () => void;
}

// ---------------------------------------------------------------------------
// Styles — Loom/Thread treatment: a timeline-row-style card with
// the yellow "waiting on you" accent the classic ApprovalCard uses,
// re-themed with variant-c tokens so the inline placement reads as
// "part of the feed" not "classic-UI fragment pasted in".
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  wrapper: {
    // Sits in the flex column BETWEEN TimelineView and CommandRail.
    // Margin mirrors how the rail's assistantLine breathes from the
    // surrounding chrome.
    margin: "10px 12px",
    padding: "12px 14px",
    background: `linear-gradient(180deg, ${surfaceColors.paper} 0%, var(--surface-raised) 100%)`,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderLeft: `4px solid ${brandColors.mid}`,
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    fontFamily: fonts.body,
    fontSize: typeScale.body,
    color: inkColors.ink,
    boxShadow: "0 6px 18px rgba(90, 81, 112, 0.1)",
    boxSizing: "border-box",
    transition: `box-shadow ${motion.transitionMs}ms ease, transform ${motion.transitionMs}ms ease`,
    ":hover": {
      boxShadow: "0 10px 24px rgba(90, 81, 112, 0.14)",
      transform: "translateY(-1px)",
    },
    "@media (max-width: 640px)": {
      margin: "8px 12px",
      padding: "10px",
      borderRadius: "8px",
      gap: "10px",
      fontSize: "12px",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "10.5px",
    fontWeight: 700,
    color: brandColors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontFamily: fonts.mono,
  },
  question: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: inkColors.ink2,
    whiteSpace: "pre-wrap",
  },
  context: {
    fontSize: "11px",
    color: inkColors.ink3,
    fontStyle: "italic",
    lineHeight: 1.4,
  },
  writeMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  },
  chip: {
    fontSize: "10.5px",
    fontWeight: 600,
    borderRadius: "999px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    padding: "2px 9px",
    color: inkColors.ink2,
    background: "var(--brand-soft)",
  },
  writeHelper: {
    fontSize: "11px",
    color: inkColors.ink3,
    lineHeight: 1.45,
  },
  writeReason: {
    fontSize: "11.5px",
    color: inkColors.ink2,
    lineHeight: 1.4,
  },
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "-4px",
  },
  toggleBtn: {
    background: "transparent",
    border: `1px solid ${surfaceColors.lineStrong}`,
    cursor: "pointer",
    fontSize: "10.5px",
    color: inkColors.ink3,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
    borderRadius: "999px",
    fontFamily: fonts.body,
    transition: "background-color 250ms ease, border-color 250ms ease",
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
      border: `1px solid ${brandColors.mid}`,
      color: inkColors.ink2,
    },
  },
  jsonError: {
    fontSize: "10px",
    color: "var(--danger)",
    fontStyle: "italic",
  },
  textarea: {
    width: "100%",
    minHeight: "100px",
    padding: "10px 12px",
    fontFamily: fonts.mono,
    fontSize: "12px",
    lineHeight: 1.5,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "8px",
    background: "var(--surface-raised)",
    color: inkColors.ink,
    resize: "vertical",
    boxSizing: "border-box",
    transition: "border-color 250ms ease, box-shadow 250ms ease",
    "&:hover": {
      border: `1px solid ${brandColors.mid}`,
    },
    "&:focus": {
      outline: "none",
      border: `1px solid ${brandColors.primary}`,
      boxShadow: `0 0 0 2px ${brandColors.glow}`,
    },
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    "@media (max-width: 480px)": {
      justifyContent: "stretch",
    },
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const UserInputInlineRow: React.FC<UserInputInlineRowProps> = ({ request, onApprove, onReject }) => {
  const styles = useStyles();

  // Hooks must run unconditionally — declare for the pending-request
  // case even when request is null, then early-return after.
  const isApiFailure = request?.inputType === "api_failure";

  const apiFailurePayload = useMemo(
    () => (isApiFailure && request ? parseApiFailurePayload(request.question) : null),
    [isApiFailure, request],
  );
  const structuredHeadline = useMemo(
    () => (isApiFailure && request ? stripJsonTail(request.question) : ""),
    [isApiFailure, request],
  );
  const hasJsonPayload = useMemo(() => (request ? /\{[\s\S]*\}/.test(request.question) : false), [request]);
  const isFreeTextRequest =
    request?.inputType === "text" ||
    request?.inputType === "free_text" ||
    ((request?.inputType === "textarea" || request?.inputType === "request_user_input") && !hasJsonPayload);

  // --- Generic branch: form vs raw-JSON dual-view -----------------------
  const [formValue, setFormValue] = useState<Record<string, JsonValue> | null>(null);
  const [rawText, setRawText] = useState<string>("");
  const [viewMode, setViewMode] = useState<"form" | "json">("form");
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!request) return;
    console.debug("[UserInputInlineRow] request received", {
      id: request.id,
      inputType: request.inputType,
      questionPreview: request.question.slice(0, 200),
      hasContext: Boolean(request.context),
      optionsCount: request.options?.length ?? 0,
    });
    if (isFreeTextRequest) {
      setRawText("");
      setFormValue(null);
      setViewMode("form");
      setJsonError(null);
      console.debug("[UserInputInlineRow] request rendered in free-text mode", {
        id: request.id,
        inputType: request.inputType,
      });
      return;
    }
    const jsonMatch = request.question.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const raw = jsonMatch[0];
      setRawText(raw);
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          setFormValue(parsed);
          setViewMode("form");
          setJsonError(null);
          return;
        }
      } catch {
        /* fall through to raw JSON view */
      }
      setFormValue(null);
      setViewMode("json");
      setJsonError(null);
      console.debug("[UserInputInlineRow] request rendered in JSON mode", {
        id: request.id,
        inputType: request.inputType,
        rawTextLength: raw.length,
      });
    } else {
      setRawText("");
      setFormValue(null);
      setViewMode("form");
      console.debug("[UserInputInlineRow] request rendered in plain form mode", {
        id: request.id,
        inputType: request.inputType,
      });
    }
  }, [isFreeTextRequest, request]);

  const questionText = useMemo(() => (request ? request.question.replace(/\{[\s\S]*\}/, "").trim() : ""), [request]);

  // Early return after hooks so the hook count stays constant
  // across renders — React requires this.
  if (!request) return null;

  const handleToggleView = (): void => {
    if (viewMode === "form" && formValue) {
      console.debug("[UserInputInlineRow] toggle form -> json", {
        id: request.id,
        inputType: request.inputType,
      });
      setRawText(JSON.stringify(formValue, null, 2));
      setJsonError(null);
      setViewMode("json");
    } else {
      try {
        const parsed = JSON.parse(rawText);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          console.debug("[UserInputInlineRow] toggle json -> form", {
            id: request.id,
            inputType: request.inputType,
            rawTextLength: rawText.length,
          });
          setFormValue(parsed);
          setJsonError(null);
          setViewMode("form");
        } else {
          setJsonError("Top-level JSON must be an object to use form view.");
        }
      } catch (e) {
        console.debug("[UserInputInlineRow] invalid JSON while toggling view", {
          id: request.id,
          inputType: request.inputType,
          rawTextPreview: rawText.slice(0, 200),
          error: e instanceof Error ? e.message : String(e),
        });
        setJsonError(`Invalid JSON: ${(e as Error).message}`);
      }
    }
  };

  const handleGenericApprove = (): void => {
    console.debug("[UserInputInlineRow] approve clicked", {
      id: request.id,
      inputType: request.inputType,
      viewMode,
      canToggle,
      rawTextLength: rawText.length,
    });
    if (viewMode === "form" && formValue) {
      onApprove(JSON.stringify(formValue));
      return;
    }
    // Validate JSON before submitting — prevents the writer from
    // sending broken payloads the backend would reject.
    try {
      const parsed = JSON.parse(rawText);
      console.debug("[UserInputInlineRow] parsed JSON approve payload", {
        id: request.id,
        inputType: request.inputType,
        rawTextLength: rawText.length,
      });
      onApprove(JSON.stringify(parsed));
      setJsonError(null);
    } catch (e) {
      console.debug("[UserInputInlineRow] invalid JSON on approve", {
        id: request.id,
        inputType: request.inputType,
        rawTextPreview: rawText.slice(0, 200),
        error: e instanceof Error ? e.message : String(e),
      });
      setJsonError(`Invalid JSON: ${(e as Error).message}`);
    }
  };

  const canToggle = Boolean(rawText) && !isFreeTextRequest;

  // ------------------------------------------------------------------
  // Branch 1 — api failure (continue / retry / stop)
  // ------------------------------------------------------------------
  if (isApiFailure && apiFailurePayload) {
    const sendAction = (action: "continue" | "retry" | "stop"): void => {
      onApprove(JSON.stringify({ action }));
    };
    const isAuth = apiFailurePayload.classification === "auth";
    return (
      <section className={styles.wrapper} aria-live="polite">
        <div className={styles.header}>
          <span>⚠ Agent service issue</span>
        </div>
        {structuredHeadline && <div className={styles.question}>{structuredHeadline}</div>}
        {request.context && <div className={styles.context}>{request.context}</div>}
        <ApiFailureView payload={apiFailurePayload} />
        <div className={styles.actions}>
          <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={() => sendAction("stop")}>
            Stop
          </Button>
          <Button appearance="secondary" size="small" onClick={() => sendAction("retry")}>
            Retry
          </Button>
          {!isAuth && (
            <Button
              appearance="primary"
              size="small"
              icon={<CheckmarkRegular />}
              onClick={() => sendAction("continue")}
            >
              Continue without
            </Button>
          )}
        </div>
      </section>
    );
  }

  // ------------------------------------------------------------------
  // Branch 2 — generic (literature_search_approval, free_text,
  // request_user_input with any payload, or any future inputType the
  // backend introduces). Falls through to JsonForm when the question
  // carries a JSON block; falls through to a plain textarea when it
  // doesn't.
  // ------------------------------------------------------------------
  return (
    <section className={styles.wrapper} aria-live="polite">
      <div className={styles.header}>
        <span>⚠ Agent needs your input</span>
      </div>
      {questionText && <div className={styles.question}>{questionText}</div>}
      {request.context && <div className={styles.context}>{request.context}</div>}
      {canToggle && (
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.toggleBtn}
            onClick={handleToggleView}
            title={viewMode === "form" ? "View as JSON" : "View as form"}
          >
            {viewMode === "form" ? <CodeRegular /> : <FormRegular />}
            <span>{viewMode === "form" ? "JSON" : "Form"}</span>
          </button>
        </div>
      )}
      {viewMode === "form" && formValue ? (
        <JsonForm value={formValue} onChange={setFormValue} />
      ) : canToggle ? (
        <textarea
          className={styles.textarea}
          value={rawText}
          onChange={(e) => {
            setRawText(e.target.value);
            if (jsonError) setJsonError(null);
          }}
          spellCheck={false}
          aria-label="Edit the JSON payload before approving"
        />
      ) : (
        // No JSON in the question → plain textarea for free-text
        // responses (``request_user_input`` with ``input_type: free_text``,
        // e.g. the drafting agent asking a clarifying question).
        <textarea
          className={styles.textarea}
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder="Type your response…"
          aria-label="Type your response"
        />
      )}
      {jsonError && <div className={styles.jsonError}>{jsonError}</div>}
      <div className={styles.actions}>
        <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={onReject}>
          Reject
        </Button>
        <Button
          appearance="primary"
          size="small"
          icon={<CheckmarkRegular />}
          onClick={canToggle ? handleGenericApprove : () => onApprove(rawText)}
        >
          Approve
        </Button>
      </div>
    </section>
  );
};

export default UserInputInlineRow;
